import { d3, cropr, gettext } from "../adapter";
import each from "lodash/each";
import extend from "lodash/extend";
import moment from "moment";
import timeFormat from "./chartTimeFormat";
import weatherStationActions from "./../actions/weatherStationActions";

var bisectDate = d3.bisector(function (d) {
  return d.timestamp;
}).left;
var formatRh = d3.format(",.1f");

// The constructor
class Chart {
  constructor(options, data, dispatch) {
    // Empty data, this is our data format
    this.data = { values: [] };
    this.radiationInterval = 0;
    this.stationId = false;
    this.dispatch = dispatch;

    // Default options
    this.options = {
      id: false,
      class: false,
      width: 500,
      height: 350,
      i18n: {
        unit: cropr.l10n.unit_precipitation_display, //I18n.t('units.mm.short'),
        unitBig: cropr.l10n.unit_precipitation_display, // I18n.t('units.mm.long'),
      },
    };

    // Merge the options
    this.options = extend(this.options, options);

    // Tooltip

    // Set chart dimension, also depending on settings as brush
    var margin = { top: 10, right: 70, bottom: 30, left: 70 };
    var height = this.options.height - (margin.top + margin.bottom);
    var width = this.options.width - (margin.left + margin.right);

    this.chart = {
      width: width,
      height: height,
      margin: margin,
    };

    this.totalChart = {
      height: this.chart.height,
      margin: {
        top: this.chart.margin.top,
      },
    };

    // Set the data
    if (data) {
      this.setData(data);
    }

    // No SVG and elements yet
    this.elements = {};

    var _this = this;
    var scrolling = false;
    var x;
    this.zoom = d3.behavior
      .zoom()
      .x(this.scales.x)
      .on("zoom", function () {
        if (_this.scales.x.domain()[0] < _this.data.minDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.minDate) +
            _this.scales.x.range()[0];
          _this.zoom.translate([x, 0]);
        } else if (_this.scales.x.domain()[1] > _this.data.maxDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.maxDate) +
            _this.scales.x.range()[1];
          _this.zoom.translate([x, 0]);
        }

        // Execute only once
        if (!scrolling) {
          _this.elements.eventRect.classed("grabbing", true);
          _this.dispatch(weatherStationActions.hovered(false));
        }

        scrolling = true;
        _this._scroll();
      })
      .on("zoomend", function () {
        if (!scrolling) return;

        _this.elements.eventRect.classed("grabbing", false);

        // Enable the pin hovers again when we stop zooming
        if (this === d3.event.sourceEvent.target) {
          _this.dispatch(weatherStationActions.hovered(true));
        }

        scrolling = false;
        _this.dispatch(
          weatherStationActions.brushDomain([
            _this.scales.x.domain()[0],
            _this.scales.x.domain()[1],
          ])
        );
      });
  }
}

// Init graph
Chart.prototype.init = function (el) {
  var _this = this;

  // Only init once and only init if we have data
  if (this.svg || !this.data) {
    return false;
  }

  // Create the SVG
  this.elements.svg = d3
    .select(el)
    .append("svg")
    .attr("class", this.options.class)
    .attr("id", this.options.id)
    .attr("width", this.options.width)
    .attr("height", this.options.height);

  var chart = (this.elements.chart = this.elements.svg
    .append("g")
    .attr("class", "precipitation chart")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")"));

  this.elements.innerChart = this.elements.svg
    .append("g")
    .attr("class", "precipitation inner")

    .attr("clip-path", "url(#clip-" + this.options.id + ")")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

  this.elements.grid = {
    x: chart
      .append("g")
      .attr("class", "precipitation grid x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "precipitation grid y"),
  };

  // Draw the axis
  this.elements.axis = {
    x: chart
      .append("g")
      .attr("class", "precipitation axis x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "precipitation axis y"),
    yRadiation: chart
      .append("g")
      .attr("class", "precipitation axis yRadiation")
      .attr("transform", "translate(" + (this.chart.width - 1) + ", 0)"),
  };

  // this.elements.bars = this.elements.innerChart.selectAll('.bar.stack');

  this.elements.legendList = d3
    .select(el)
    .append("ul")
    .attr("class", "l-stacked legend has-left-spacing");

  this.elements.legendRadiation = this.elements.legendList.append("li").attr("class", "legend");
  var radiationSpan = this.elements.legendRadiation.append("span").attr("class", "precipitation");
  radiationSpan.append("span").attr("class", "precipitation1");
  radiationSpan.append("span").attr("class", "precipitation2");
  radiationSpan.append("span").attr("class", "precipitation3");

  this.elements.legendSpan = this.elements.legendRadiation
    .append("span")
    .attr("class", "content")
    .html(
      gettext("Precipitation in {{unit}} per {{interval}} hour")
        .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
        .replace("{{interval}}", this.radiationInterval)
    );

  this.elements.legendLine = this.elements.legendList.append("li").attr("class", "legend first");
  this.elements.legendLine.append("span").attr("class", "precipChance line").html("&nbsp;");
  this.elements.legendLine
    .append("span")
    .attr("class", "content")
    .html(gettext("Precipitation probability"));

  this.elements.chartCover = this.elements.svg
    .append("g")
    .attr("class", "cover")
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    );

  // Draw the mouse vertical line
  var clipId = "clip";

  // Draw the mouse vertical line
  this.elements.mouse = this.elements.chartCover
    .append("g")
    .attr("clip-path", "url(#" + clipId + "-" + this.options.id + ")")
    .append("g")
    .attr("class", "mouse")
    .style("opacity", "0");

  this.elements.mouse
    .append("line")
    .attr("class", "line mouse")
    .attr("x1", 2)
    .attr("y1", 8)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  this.elements.mouse
    .append("circle")
    .attr("class", "circle mouse")
    .attr("cx", 2)
    .attr("cy", 0)
    .attr("r", 6);

  this.elements.tooltip = this.elements.mouse.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipBalloon = this.elements.tooltip.append("rect").attr("rx", 2).attr("ry", 2);
  this.elements.tooltip1 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");

  // this.elements.tooltip1.append('rect').style('fill', 'red').attr('width', 30).attr('height', 30)
  this.elements.tooltipText1 = this.elements.tooltip1.append("text").attr("class", "tooltipText");
  this.elements.tooltip2 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText2 = this.elements.tooltip2.append("text").attr("class", "tooltipText");
  this.elements.tooltip3 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText3 = this.elements.tooltip3.append("text").attr("class", "tooltipText");

  this.elements.eventRect = this.elements.svg
    .append("svg:rect")
    .attr("class", "rect event")
    .attr("width", this.chart.width)
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    )
    .attr("height", this.totalChart.height)
    .attr("fill", "none")
    .attr("pointer-events", "all")
    .on("mouseout", function () {
      // on mouse out hide line, circles and text
      _this.dispatch(weatherStationActions.hovered(false));
    })
    .on("mouseover", function () {
      // on mouse in show line, circles and text
      _this.dispatch(weatherStationActions.hovered(true));
    })
    .on("mousemove", function () {
      // mouse moving over canvas
      //if(d3.event.which) return;
      var mouseX = d3.mouse(this)[0]; // mouse position in x
      var date = _this.scales.x.invert(mouseX); // date corresponding to mouse x
      _this.dispatch(weatherStationActions.hoverDate(date));
    })
    .on("click", function () {
      if (d3.event.defaultPrevented) return;
      _this.dispatch(weatherStationActions.togglePin());
    })
    .call(this.zoom)
    .on("wheel.zoom", null)
    .on("MozMousePixelScroll.zoom", null)
    .on("mousewheel.zoom", null)
    .on("dblclick.zoom", null);

  // Draw the clippaths
  this._drawClipPaths();
  this.update();
};

// Set the data we need, we expect to receive it in the correct format
Chart.prototype.setData = function (data) {
  // Set the data
  this.data = extend(this.data, data);

  // Set min and max data value
  this.data.minDate = this.data.station.measurements.airTemperature[0].timestamp;
  this.data.maxDate =
    this.data.station.measurements.airTemperature[
      this.data.station.measurements.airTemperature.length - 1
    ].timestamp;

  // Create or update scales and axis etc.
  this._setScales();
  this._setAxis();

  this._setDataShapes();
};

Chart.prototype.updateHoverState = function (hovered, pinned, pinDate) {
  if (hovered && !pinned) {
    this.elements.mouse.style("opacity", ".5");
    this.elements.eventRect.classed("grab", false);
  } else if (!pinned) {
    this.elements.mouse.style("opacity", "0");
  } else {
    this.data.pinDate = pinDate;
    this.elements.mouse.style("opacity", "1");
    this.elements.eventRect.classed("grab", true);
  }

  this.data.pinned = pinned;
};

Chart.prototype.updateHoverDate = function (date, pinned) {
  if (!pinned) {
    var xPos = this.scales.x(date);
    this.elements.mouse.attr("transform", "translate(" + (xPos - 2) + "," + 0 + ")");

    var dataRh = this.data.station.forecast.precipChance;
    var i = bisectDate(dataRh, date, 1);
    var d0 = dataRh[i - 1];
    var d1 = dataRh[i];
    var dPC = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;

    var dataRadiation = this.data.compressedData;
    i = bisectDate(dataRadiation, date, 1);
    d0 = dataRadiation[i - 1];
    d1 = dataRadiation[i];
    var dPrecip = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;
    var dtool = new Date(date);

    if (date) {
      dtool.setMinutes(Math.floor(date.getMinutes() / 15) * 15);
    }
    this.elements.tooltipText1.text(moment(dtool).format("L LT"));
    this.elements.tooltipText2.text(cropr.toLocaleFixed(formatRh(dPC.y)) + "%");
    this.elements.tooltipText3.text(
      cropr.l10n_utils.mm_to_l10n(dPrecip.y) + " " + cropr.l10n.unit_precipitation_display
    );

    // Calculate the bbox
    var tooltipMarginLeftRight = 5; // 5px margin
    var tooltipMarginTopBottom = 4; // 5px margin

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltip1.attr("transform", "translate(21, 9)");
      this.elements.tooltipText1.attr("text-anchor", "left");
      this.elements.tooltip2.attr("transform", "translate(21, 24)");
      this.elements.tooltipText2.attr("text-anchor", "left");
      this.elements.tooltip3.attr("transform", "translate(21, 39)");
      this.elements.tooltipText3.attr("text-anchor", "left");

      // this.elements.tooltipBalloon.attr('x', bboxX - tooltipMarginLeftRight + 20)
    } else {
      this.elements.tooltip1.attr("transform", "translate(-20, 9)");
      this.elements.tooltipText1.attr("text-anchor", "end");
      this.elements.tooltip2.attr("transform", "translate(-20, 24)");
      this.elements.tooltipText2.attr("text-anchor", "end");
      this.elements.tooltip3.attr("transform", "translate(-20, 39)");
      this.elements.tooltipText3.attr("text-anchor", "end");

      // this.elements.tooltipBalloon.attr('x', bboxX - tooltipMarginLeftRight - 20)
    }

    var bbox1 = this.elements.tooltipText1[0][0].getBBox();
    var bbox2 = this.elements.tooltipText2[0][0].getBBox();

    if (bbox1.width > bbox2.width) {
      var bboxWidth = bbox1.width + 2 * tooltipMarginLeftRight;
      var bboxX = bbox1.x;
    } else {
      bboxWidth = bbox2.width + 2 * tooltipMarginLeftRight;
      bboxX = bbox2.x;
    }

    this.elements.tooltipBalloon
      .attr("y", bbox1.y - tooltipMarginTopBottom + 9)
      .attr("width", bboxWidth)
      .attr("height", bbox1.height * 3 + 2 * tooltipMarginTopBottom);

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight + 20);
    } else {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight - 20);
    }
  }
};

// Update
Chart.prototype.update = function (data) {
  var _this = this;

  if (data) {
    // DIE SETDATA FUNCTIE BETER BEKIJKEN
    this.setData(data);
    this.zoom.x(this.scales.x);
    this.scales.x.domain(data.domain);
  }

  var redraw = false;
  if (this.data.station.id !== this.stationId) {
    redraw = true;
    this.stationId = this.data.station.id;
  }

  var relativeHumidity = this.data.station.forecast.precipChance;
  var compressedData = this._compressBatched(this.data.station.forecast.precipitation, redraw);

  var maxYDomain = d3.max(
    compressedData.map(function (d) {
      return d.y;
    })
  );
  if (cropr.l10n.unit_precipitation === "in") {
    if (!maxYDomain) maxYDomain = 0.05;
    if (maxYDomain < 0.05) maxYDomain = 0.05;
  } else {
    if (!maxYDomain) maxYDomain = 2;
    if (maxYDomain < 2) maxYDomain = 2;
  }
  this.scales.yRadiation.domain([0, maxYDomain * this.radiationInterval]);

  //  this.scales.yRadiation.domain([0, 6 * this.radiationInterval]);

  var selection = this.elements.innerChart.selectAll(".bar.stack").data(compressedData);
  selection.enter().append("rect").attr("class", "precipitation bar stack");
  selection.exit().remove();

  this.elements.axis.x.transition().call(this.axis.x);
  this.elements.axis.y.transition().call(this.axis.y);
  this.elements.axis.yRadiation.transition().call(this.axis.yRadiation);
  this.elements.grid.x.transition().call(this.axis.gridX);
  this.elements.grid.y.transition().call(this.axis.gridY);

  this.elements.axis.yRadiation.attr("transform", "translate(" + (this.chart.width - 1) + ", 0)");

  this.elements.eventRect.attr("width", this.chart.width);

  selection
    .transition()
    .attr("x", function (d) {
      return _this.scales.x(d.timestamp);
    })
    .attr("y", function (d) {
      return _this.scales.yRadiation(d.y);
    })
    .attr("height", function (d) {
      return _this.chart.height - _this.scales.yRadiation(d.y);
    })
    .attr("width", 3);

  // Draw the temp line
  if (!this.elements.line) {
    this.elements.line = this.elements.innerChart.append("path").attr("class", "line precipChance");
  }

  // Update the line graph data
  this.elements.line.datum(relativeHumidity);

  this.elements.line.transition().attr("d", this.shapes.line);

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  if (this.data.pinned) {
    this.elements.mouse
      .transition()
      .attr("transform", "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")");
  }

  this.elements.legendSpan.html(
    gettext("Precipitation in {{unit}} per {{interval}} hour")
      .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
      .replace("{{interval}}", this.radiationInterval)
  );
};

// Not really DRY. The same as update besides the transitions
Chart.prototype._scroll = function () {
  var _this = this;

  var relativeHumidity = this.data.station.forecast.precipChance;

  // Update the line graph data
  this.elements.line.datum(relativeHumidity);

  this.elements.axis.x.call(this.axis.x);
  this.elements.axis.y.call(this.axis.y);
  this.elements.axis.yRadiation.call(this.axis.yRadiation);
  this.elements.grid.x.call(this.axis.gridX);
  this.elements.grid.y.call(this.axis.gridY);

  this.elements.line.attr("d", this.shapes.line);

  this.elements.innerChart
    .selectAll(".bar.stack")
    .attr("x", function (d) {
      return _this.scales.x(d.timestamp);
    })
    .attr("y", function (d) {
      return _this.scales.yRadiation(d.y);
    });

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  if (this.data.pinned) {
    this.elements.mouse.attr(
      "transform",
      "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")"
    );
  }
};

Chart.prototype.resize = function (width, height) {
  if (width) {
    this.options.width = width;
    this.chart.width = this.options.width - (this.chart.margin.left + this.chart.margin.right);
  }

  if (height) {
    this.options.height = height;
    this.chart.width = this.options.height - (this.chart.margin.top + this.chart.margin.bottom);
  }

  this.elements.svg.style("width", this.options.width);
  this.elements.svg.style("height", this.options.height);

  // Update the scales
  this._setScales();
  this._setAxis();
  this.update();
};

Chart.prototype.destroy = function () {
  this.elements.svg.remove();
};

Chart.prototype._setScales = function () {
  var domain = this.data.domain;
  //var maxYRadiationDomain = d3.max(this.data.station.measurements.radiation.map(function(d) { return !isNaN(d.y) ? d.y : 0; }));

  //var minYRadiationDomain = d3.min(this.data.station.measurements.radiation.map(function(d) { return !isNaN(d.y) ? d.y : 0; }));

  // if (!maxYRadiationDomain) maxYRadiationDomain = 10;
  // if (!minYRadiationDomain) minYRadiationDomain = 0;

  var maxYRadiationDomain = 6 * this.radiationInterval;
  var minYRadiationDomain = 0;

  var maxYDomain = 100;
  var minYDomain = 0;

  this.scales = {
    x: d3.time.scale().range([0, this.chart.width]).domain(domain),

    y: d3.scale.linear().range([this.chart.height, 0]).domain([minYDomain, maxYDomain]),

    yRadiation: d3.scale
      .linear()
      .range([this.chart.height, 0])
      .domain([minYRadiationDomain, maxYRadiationDomain]),
  };
};

Chart.prototype._setAxis = function () {
  // Calculate the amount of ticks
  var ticksY = Math.round(this.chart.height / 40);
  var ticksX = Math.round(this.chart.width / 150);

  this.axis = {
    x: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat(timeFormat.chart),

    y: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return d + "%";
      }),

    yRadiation: d3.svg
      .axis()
      .scale(this.scales.yRadiation)
      .orient("right")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return cropr.l10n_utils.mm_to_l10n(d) + " " + cropr.l10n.unit_precipitation_display;
      }),

    gridX: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.chart.height, 0, 0)
      .tickFormat(""),
    gridY: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY * 2)
      .tickSize(-this.chart.width, 0, 0)
      .tickFormat(""),
  };
};

Chart.prototype._setDataShapes = function () {
  var _this = this;

  this.shapes = {
    line: d3.svg
      .line()
      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y(function (d) {
        return _this.scales.y(!isNaN(d.y) ? d.y : 0);
      }),
  };
};

Chart.prototype._compressBatched = function (data, redraw) {
  // Interval in hours
  // To be calulated
  var interval = 1; // Data is per kwartier, 4 intervals is een uur
  var barWidth = 3;

  // How many bars are going to fit insite the current chart
  var fittingBars = this.chart.width / barWidth;

  // How many hours is our current domain
  var domainStart = moment(this.data.domain[0]);
  var domainEnd = moment(this.data.domain[1]);

  var diff = domainEnd.diff(domainStart, "hours");

  while (fittingBars * interval < diff) {
    interval++;
  }
  if (interval > 12) {
    interval = 24;
  } else if (interval > 6) {
    interval = 12;
  } else if (interval > 4) {
    interval = 6;
  }

  // Only recalculate if neccesary
  if (this.radiationInterval === interval && !redraw) {
    return this.data.compressedData;
  }

  this.radiationInterval = interval;

  var compressedData = [];
  var sum = 0;
  var maxTime = false;

  each(data, function (value) {
    var time = value.timestamp;
    if (!maxTime) {
      maxTime = new Date(time);
      var correction = time.getHours() % interval;
      maxTime.setMinutes(0);
      if (interval === 1) {
        maxTime.setTime(maxTime.getTime());
      } else {
        maxTime.setTime(maxTime.getTime() + (interval - correction) * 3600 * 1000);
      }
    }
    if (maxTime <= time) {
      // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
      var averageTime = new Date(maxTime);
      averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

      compressedData.push({
        timestamp: averageTime,
        y: sum,
      });

      sum = 0;

      maxTime = false;
    }

    // @TODO: before adding the value to the sum, check if the measurement is active
    sum += value.y;
  });

  // The last push, with the remaining rain fall
  if (maxTime) {
    // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
    var averageTime = new Date(maxTime);
    averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

    compressedData.push({
      timestamp: averageTime,
      y: sum,
    });
  }

  this.data.compressedData = compressedData;
  return compressedData;
};

Chart.prototype._drawClipPaths = function () {
  var defs;
  this.defs = defs = this.elements.svg.append("defs");

  this.defs.clip = defs
    .append("svg:clipPath")
    .attr("id", "clip-" + this.options.id)
    .append("svg:rect")
    .attr("x", 0)
    .attr("y", -8)
    .attr("height", this.chart.height + 8);

  this.defs.totalClip = defs
    .append("svg:clipPath")
    .attr("id", "clip-total-" + this.options.id)
    .append("svg:rect")
    .attr("x", "0")
    .attr("y", "-8")
    .attr("height", this.totalChart.height + 8);

  this.defs.mask = defs
    .append("svg:mask")
    .attr("id", "mask-" + this.options.id)
    .attr("width", this.chart.width)
    .attr("height", this.chart.height + 8)
    .attr("x", 0)
    .attr("y", -8);
};

export default Chart;
